import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowLeft,
  Edit3,
  Copy,
  Trash2,
  CheckCircle,
  XCircle,
  BarChart3,
  FileText,
  AlertTriangle,
  Settings,
  Calendar,
  Clock,
  User,
  Sparkles,
  Scale,
  Percent,
  Shield,
  Tag,
  MapPin,
  Users,
  Package,
  ChevronRight,
  Zap,
  MoreVertical,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  getPricingRuleByIdThunk,
  deletePricingRuleThunk,
  togglePricingRuleStatusThunk,
} from "../../store/thunks/pricingThunks";
import {
  selectCurrentPricingRule,
  selectPricingRulesLoading,
  selectPricingRules,
} from "../../store/slices/pricingSlice";
import RuleAnalytics from "./components/RuleAnalytics";
import AuditLog from "./components/AuditLog";
import ConflictDetection from "./components/ConflictDetection";

// Skeleton Loader
const DetailsSkeleton = () => (
  <div className="min-h-screen bg-gradient-to-br from-neutral-lightest via-white to-primary/5 p-4 lg:p-6">
    <div className="max-w-7xl mx-auto animate-pulse">
      <div className="h-6 w-40 bg-neutral-light/50 rounded mb-6" />
      <div className="bg-white rounded-2xl p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="space-y-3">
            <div className="h-8 w-64 bg-neutral-light/50 rounded" />
            <div className="h-4 w-96 bg-neutral-light/30 rounded" />
          </div>
          <div className="flex gap-2">
            <div className="h-10 w-24 bg-neutral-light/50 rounded-xl" />
            <div className="h-10 w-24 bg-neutral-light/50 rounded-xl" />
          </div>
        </div>
      </div>
      <div className="bg-white rounded-2xl p-6">
        <div className="flex gap-4 mb-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-10 w-28 bg-neutral-light/50 rounded-xl" />
          ))}
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-neutral-light/30 rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Info Card Component
const InfoCard = ({ icon: Icon, label, value, color = "primary" }) => (
  <div className="bg-white rounded-xl p-4 border border-neutral-light/50 hover:shadow-md transition-all">
    <div className="flex items-start gap-3">
      <div
        className={`w-10 h-10 rounded-xl bg-${color}/10 flex items-center justify-center flex-shrink-0`}
      >
        <Icon className={`w-5 h-5 text-${color}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-neutral mb-1">{label}</p>
        <p className="text-sm font-semibold text-neutral-dark truncate">
          {value || "—"}
        </p>
      </div>
    </div>
  </div>
);

// Tag Badge Component
const TagBadge = ({ children, color = "primary" }) => (
  <span
    className={`inline-flex items-center px-3 py-1 bg-${color}/10 text-${color} rounded-lg text-sm font-medium`}
  >
    {children}
  </span>
);

// Section Component
const Section = ({ title, icon: Icon, children, color = "primary" }) => (
  <div className="bg-white rounded-2xl border border-neutral-light/50 overflow-hidden">
    <div
      className={`px-5 py-4 border-b border-neutral-light/50 bg-gradient-to-r from-${color}/5 to-transparent`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-8 h-8 rounded-lg bg-${color}/10 flex items-center justify-center`}
        >
          <Icon className={`w-4 h-4 text-${color}`} />
        </div>
        <h3 className="font-semibold text-neutral-dark">{title}</h3>
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

// Charge Card Component
const ChargeCard = ({ title, icon: Icon, color, children }) => (
  <div
    className={`rounded-xl border-2 border-${color}/20 bg-gradient-to-br from-${color}/5 to-white overflow-hidden`}
  >
    <div
      className={`px-4 py-3 border-b border-${color}/10 flex items-center gap-2`}
    >
      <Icon className={`w-4 h-4 text-${color}`} />
      <h4 className={`text-sm font-semibold text-${color}`}>{title}</h4>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const PricingRuleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const rule = useSelector(selectCurrentPricingRule);
  const loading = useSelector(selectPricingRulesLoading);
  const allRules = useSelector(selectPricingRules);

  const [activeTab, setActiveTab] = useState("overview");
  const [showActions, setShowActions] = useState(false);

  useEffect(() => {
    dispatch(getPricingRuleByIdThunk(id));
  }, [dispatch, id]);

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this pricing rule?")) {
      try {
        await dispatch(deletePricingRuleThunk(id)).unwrap();
        toast.success("Pricing rule deleted successfully");
        navigate("/pricing-rules");
      } catch (error) {
        toast.error(error || "Failed to delete pricing rule");
      }
    }
  };

  const handleToggleStatus = async () => {
    try {
      await dispatch(togglePricingRuleStatusThunk(id)).unwrap();
      toast.success(
        `Rule ${rule.isActive ? "deactivated" : "activated"} successfully`
      );
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  if (loading || !rule) {
    return <DetailsSkeleton />;
  }

  const tabs = [
    { id: "overview", label: "Overview", icon: Settings },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
    { id: "conflicts", label: "Conflicts", icon: AlertTriangle },
    { id: "audit", label: "Audit Log", icon: FileText },
  ];

  // Check expiry status
  const isExpired = rule.validUntil && new Date(rule.validUntil) < new Date();
  const isExpiringSoon =
    rule.validUntil &&
    !isExpired &&
    Math.ceil(
      (new Date(rule.validUntil) - new Date()) / (1000 * 60 * 60 * 24)
    ) <= 7;

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-lightest via-white to-primary/5">
      {/* Header */}
      <div className="bg-white border-b border-neutral-light/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-4">
          {/* Back Button */}
          <button
            onClick={() => navigate("/pricing-rules")}
            className="flex items-center gap-2 text-neutral hover:text-primary transition-colors mb-4 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-medium">Back to Pricing Rules</span>
          </button>

          {/* Title Row */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h1 className="text-2xl lg:text-3xl font-bold text-neutral-dark truncate">
                  {rule.name}
                </h1>

                {/* Status Badge */}
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${
                    isExpired
                      ? "bg-danger/10 text-danger"
                      : rule.isActive
                      ? "bg-success/10 text-success-dark"
                      : "bg-neutral-light text-neutral-dark"
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full ${
                      isExpired
                        ? "bg-danger"
                        : rule.isActive
                        ? "bg-success animate-pulse"
                        : "bg-neutral"
                    }`}
                  />
                  {isExpired
                    ? "Expired"
                    : rule.isActive
                    ? "Active"
                    : "Inactive"}
                </span>

                {/* Priority Badge */}
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                    rule.priority >= 80
                      ? "bg-danger/10 text-danger"
                      : rule.priority >= 50
                      ? "bg-warning/10 text-warning-dark"
                      : "bg-primary/10 text-primary"
                  }`}
                >
                  Priority {rule.priority}
                </span>

                {/* Expiring Soon Warning */}
                {isExpiringSoon && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-warning/10 text-warning-dark rounded-full text-xs font-medium">
                    <AlertTriangle className="w-3 h-3" />
                    Expiring Soon
                  </span>
                )}
              </div>

              {rule.description && (
                <p className="text-neutral text-sm lg:text-base max-w-2xl">
                  {rule.description}
                </p>
              )}
            </div>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center gap-2">
              <button
                onClick={handleToggleStatus}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                  rule.isActive
                    ? "bg-warning/10 text-warning-dark hover:bg-warning hover:text-white"
                    : "bg-success/10 text-success-dark hover:bg-success hover:text-white"
                }`}
              >
                {rule.isActive ? (
                  <XCircle className="w-4 h-4" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                <span>{rule.isActive ? "Deactivate" : "Activate"}</span>
              </button>

              <button
                onClick={() => navigate(`/pricing-rules/${id}/edit`)}
                className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-xl font-medium hover:bg-primary-dark transition-colors shadow-sm"
              >
                <Edit3 className="w-4 h-4" />
                <span>Edit</span>
              </button>

              <button
                onClick={() => navigate(`/pricing-rules/${id}/clone`)}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-neutral-light text-neutral-dark rounded-xl font-medium hover:bg-neutral-lightest transition-colors"
              >
                <Copy className="w-4 h-4" />
                <span>Clone</span>
              </button>

              <button
                onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-2.5 bg-danger/10 text-danger rounded-xl font-medium hover:bg-danger hover:text-white transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </button>
            </div>

            {/* Mobile Actions Menu */}
            <div className="lg:hidden relative">
              <button
                onClick={() => setShowActions(!showActions)}
                className="p-2 rounded-xl bg-neutral-lightest text-neutral-dark hover:bg-neutral-light transition-colors"
              >
                <MoreVertical className="w-5 h-5" />
              </button>

              {showActions && (
                <>
                  <div
                    className="fixed inset-0 z-20"
                    onClick={() => setShowActions(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-neutral-light/50 py-2 z-30">
                    <button
                      onClick={() => {
                        handleToggleStatus();
                        setShowActions(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-neutral-lightest transition-colors"
                    >
                      {rule.isActive ? (
                        <XCircle className="w-4 h-4 text-warning" />
                      ) : (
                        <CheckCircle className="w-4 h-4 text-success" />
                      )}
                      <span>{rule.isActive ? "Deactivate" : "Activate"}</span>
                    </button>
                    <button
                      onClick={() => {
                        navigate(`/pricing-rules/${id}/edit`);
                        setShowActions(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-neutral-lightest transition-colors"
                    >
                      <Edit3 className="w-4 h-4 text-primary" />
                      <span>Edit Rule</span>
                    </button>
                    <button
                      onClick={() => {
                        navigate(`/pricing-rules/${id}/clone`);
                        setShowActions(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-neutral-lightest transition-colors"
                    >
                      <Copy className="w-4 h-4 text-neutral" />
                      <span>Clone Rule</span>
                    </button>
                    <div className="my-2 border-t border-neutral-light/50" />
                    <button
                      onClick={() => {
                        handleDelete();
                        setShowActions(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-danger hover:bg-danger-light transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Delete Rule</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-sm border border-neutral-light/50 mb-6 overflow-hidden">
          <div className="flex overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${
                  activeTab === tab.id
                    ? "border-primary text-primary bg-primary/5"
                    : "border-transparent text-neutral hover:text-neutral-dark hover:bg-neutral-lightest/50"
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === "overview" && <OverviewTab rule={rule} />}
          {activeTab === "analytics" && (
            <RuleAnalytics ruleId={rule._id} ruleName={rule.name} />
          )}
          {activeTab === "conflicts" && (
            <ConflictDetection currentRule={rule} allRules={allRules} />
          )}
          {activeTab === "audit" && (
            <AuditLog ruleId={rule._id} ruleName={rule.name} />
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== OVERVIEW TAB ====================
const OverviewTab = ({ rule }) => {
  // Format charge value
  const formatChargeValue = (charge) => {
    if (!charge || charge.value === null || charge.value === undefined)
      return "—";
    if (charge.type === "PERCENTAGE") return `${charge.value}%`;
    if (charge.type === "PER_GRAM") return `₹${charge.value}/gm`;
    if (charge.type === "PER_CARAT") return `₹${charge.value}/ct`;
    return `₹${charge.value}`;
  };

  return (
    <div className="space-y-6">
      {/* Quick Info Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <InfoCard
          icon={Zap}
          label="Priority Level"
          value={rule.priority}
          color={
            rule.priority >= 80
              ? "danger"
              : rule.priority >= 50
              ? "warning"
              : "primary"
          }
        />
        <InfoCard
          icon={Calendar}
          label="Valid From"
          value={new Date(rule.validFrom).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
          color="success"
        />
        <InfoCard
          icon={Clock}
          label="Valid Until"
          value={
            rule.validUntil
              ? new Date(rule.validUntil).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })
              : "No Expiry"
          }
          color="warning"
        />
        <InfoCard
          icon={User}
          label="Created By"
          value={rule.createdBy?.adminName || "System"}
          color="secondary"
        />
      </div>

      {/* Conditions Section */}
      {rule.conditions && (
        <Section title="Conditions" icon={Settings} color="primary">
          <div className="space-y-4">
            {/* Product Types */}
            {rule.conditions.productTypes?.length > 0 && (
              <div className="flex flex-wrap items-start gap-3">
                <span className="text-sm font-medium text-neutral min-w-[120px] pt-1">
                  Product Types
                </span>
                <div className="flex flex-wrap gap-2">
                  {rule.conditions.productTypes.map((type, idx) => (
                    <TagBadge key={idx} color="primary">
                      {type}
                    </TagBadge>
                  ))}
                </div>
              </div>
            )}

            {/* Sub Types */}
            {rule.conditions.subTypes?.length > 0 && (
              <div className="flex flex-wrap items-start gap-3">
                <span className="text-sm font-medium text-neutral min-w-[120px] pt-1">
                  Sub Types
                </span>
                <div className="flex flex-wrap gap-2">
                  {rule.conditions.subTypes.map((type, idx) => (
                    <TagBadge key={idx} color="secondary">
                      {type}
                    </TagBadge>
                  ))}
                </div>
              </div>
            )}

            {/* Categories */}
            {rule.conditions.categories?.length > 0 && (
              <div className="flex flex-wrap items-start gap-3">
                <span className="text-sm font-medium text-neutral min-w-[120px] pt-1">
                  Categories
                </span>
                <div className="flex flex-wrap gap-2">
                  {rule.conditions.categories.map((cat, idx) => (
                    <TagBadge key={idx} color="secondary">
                      {cat}
                    </TagBadge>
                  ))}
                </div>
              </div>
            )}

            {/* Purity Levels */}
            {rule.conditions.purityLevels?.length > 0 && (
              <div className="flex flex-wrap items-start gap-3">
                <span className="text-sm font-medium text-neutral min-w-[120px] pt-1">
                  Purity Levels
                </span>
                <div className="flex flex-wrap gap-2">
                  {rule.conditions.purityLevels.map((purity, idx) => (
                    <TagBadge key={idx} color="success">
                      {purity}
                    </TagBadge>
                  ))}
                </div>
              </div>
            )}

            {/* Collections */}
            {rule.conditions.collections?.length > 0 && (
              <div className="flex flex-wrap items-start gap-3">
                <span className="text-sm font-medium text-neutral min-w-[120px] pt-1">
                  Collections
                </span>
                <div className="flex flex-wrap gap-2">
                  {rule.conditions.collections.map((col, idx) => (
                    <TagBadge key={idx} color="secondary">
                      {col}
                    </TagBadge>
                  ))}
                </div>
              </div>
            )}

            {/* Branch Codes */}
            {rule.conditions.branchCodes?.filter((b) => b !== null).length >
              0 && (
              <div className="flex flex-wrap items-start gap-3">
                <span className="text-sm font-medium text-neutral min-w-[120px] pt-1">
                  Branches
                </span>
                <div className="flex flex-wrap gap-2">
                  {rule.conditions.branchCodes
                    .filter((b) => b !== null)
                    .map((branch, idx) => (
                      <TagBadge key={idx} color="warning">
                        {branch}
                      </TagBadge>
                    ))}
                </div>
              </div>
            )}

            {/* Customer Types */}
            {rule.conditions.customerTypes?.length > 0 && (
              <div className="flex flex-wrap items-start gap-3">
                <span className="text-sm font-medium text-neutral min-w-[120px] pt-1">
                  Customer Types
                </span>
                <div className="flex flex-wrap gap-2">
                  {rule.conditions.customerTypes.map((type, idx) => (
                    <TagBadge key={idx} color="success">
                      {type}
                    </TagBadge>
                  ))}
                </div>
              </div>
            )}

            {/* Weight & Quantity Range */}
            {(rule.conditions.minWeight ||
              rule.conditions.maxWeight ||
              rule.conditions.minQuantity ||
              rule.conditions.maxQuantity) && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-neutral-light/50">
                {rule.conditions.minWeight && (
                  <div className="bg-neutral-lightest/50 rounded-xl p-3">
                    <p className="text-xs text-neutral mb-1">Min Weight</p>
                    <p className="font-semibold text-neutral-dark">
                      {rule.conditions.minWeight} gm
                    </p>
                  </div>
                )}
                {rule.conditions.maxWeight && (
                  <div className="bg-neutral-lightest/50 rounded-xl p-3">
                    <p className="text-xs text-neutral mb-1">Max Weight</p>
                    <p className="font-semibold text-neutral-dark">
                      {rule.conditions.maxWeight} gm
                    </p>
                  </div>
                )}
                {rule.conditions.minQuantity && (
                  <div className="bg-neutral-lightest/50 rounded-xl p-3">
                    <p className="text-xs text-neutral mb-1">Min Quantity</p>
                    <p className="font-semibold text-neutral-dark">
                      {rule.conditions.minQuantity}
                    </p>
                  </div>
                )}
                {rule.conditions.maxQuantity && (
                  <div className="bg-neutral-lightest/50 rounded-xl p-3">
                    <p className="text-xs text-neutral mb-1">Max Quantity</p>
                    <p className="font-semibold text-neutral-dark">
                      {rule.conditions.maxQuantity}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </Section>
      )}

      {/* Charges Section */}
      {rule.charges && (
        <Section title="Charges Configuration" icon={Percent} color="secondary">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Making Charges */}
            {rule.charges.makingCharges && (
              <ChargeCard
                title="Making Charges"
                icon={Sparkles}
                color="primary"
              >
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-neutral">Type</span>
                    <span className="font-medium text-neutral-dark">
                      {rule.charges.makingCharges.type}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-neutral">Value</span>
                    <span className="text-lg font-bold text-primary">
                      {formatChargeValue(rule.charges.makingCharges)}
                    </span>
                  </div>
                  {rule.charges.makingCharges.minAmount && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-neutral">Min Amount</span>
                      <span className="font-medium text-neutral-dark">
                        ₹{rule.charges.makingCharges.minAmount}
                      </span>
                    </div>
                  )}
                  {rule.charges.makingCharges.maxAmount && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-neutral">Max Amount</span>
                      <span className="font-medium text-neutral-dark">
                        ₹{rule.charges.makingCharges.maxAmount}
                      </span>
                    </div>
                  )}

                  {/* Slabs */}
                  {rule.charges.makingCharges.slabs?.length > 0 && (
                    <div className="pt-3 border-t border-neutral-light/50">
                      <p className="text-xs font-medium text-neutral uppercase tracking-wide mb-2">
                        Weight Slabs
                      </p>
                      <div className="space-y-2">
                        {rule.charges.makingCharges.slabs.map((slab, idx) => (
                          <div
                            key={idx}
                            className="flex justify-between items-center text-sm bg-white rounded-lg p-2"
                          >
                            <span className="text-neutral">
                              {slab.minWeight}g - {slab.maxWeight}g
                            </span>
                            <span className="font-semibold text-primary">
                              {slab.type === "PERCENTAGE"
                                ? `${slab.value}%`
                                : `₹${slab.value}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </ChargeCard>
            )}

            {/* Wastage Charges */}
            {rule.charges.wastageCharges && (
              <ChargeCard title="Wastage Charges" icon={Scale} color="warning">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-neutral">Type</span>
                    <span className="font-medium text-neutral-dark">
                      {rule.charges.wastageCharges.type}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-neutral">Value</span>
                    <span className="text-lg font-bold text-warning-dark">
                      {formatChargeValue(rule.charges.wastageCharges)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-neutral">
                      Include Stone Weight
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        rule.charges.wastageCharges.includeStoneWeight
                          ? "bg-success/10 text-success-dark"
                          : "bg-neutral-light text-neutral"
                      }`}
                    >
                      {rule.charges.wastageCharges.includeStoneWeight
                        ? "Yes"
                        : "No"}
                    </span>
                  </div>
                </div>
              </ChargeCard>
            )}

            {/* Hallmark Charges */}
            {rule.charges.hallmarkCharges && (
              <ChargeCard
                title="Hallmark Charges"
                icon={Shield}
                color="success"
              >
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-neutral">Enabled</span>
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        rule.charges.hallmarkCharges.enabled
                          ? "bg-success/10 text-success-dark"
                          : "bg-neutral-light text-neutral"
                      }`}
                    >
                      {rule.charges.hallmarkCharges.enabled ? "Yes" : "No"}
                    </span>
                  </div>
                  {rule.charges.hallmarkCharges.enabled && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-neutral">Type</span>
                        <span className="font-medium text-neutral-dark">
                          {rule.charges.hallmarkCharges.type}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-neutral">Value</span>
                        <span className="text-lg font-bold text-success-dark">
                          {formatChargeValue(rule.charges.hallmarkCharges)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </ChargeCard>
            )}

            {/* Stone Charges */}
            {rule.charges.stoneCharges && (
              <ChargeCard
                title="Stone Charges"
                icon={Package}
                color="secondary"
              >
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-neutral">Type</span>
                    <span className="font-medium text-neutral-dark">
                      {rule.charges.stoneCharges.type}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-neutral">Value</span>
                    <span className="text-lg font-bold text-secondary-dark">
                      {formatChargeValue(rule.charges.stoneCharges)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-neutral">
                      Use Market Rate
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        rule.charges.stoneCharges.useMarketRate
                          ? "bg-success/10 text-success-dark"
                          : "bg-neutral-light text-neutral"
                      }`}
                    >
                      {rule.charges.stoneCharges.useMarketRate ? "Yes" : "No"}
                    </span>
                  </div>
                  {rule.charges.stoneCharges.marginOnMarketRate !== null && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-neutral">Margin</span>
                      <span className="font-medium text-neutral-dark">
                        {rule.charges.stoneCharges.marginOnMarketRate}%
                      </span>
                    </div>
                  )}
                </div>
              </ChargeCard>
            )}
          </div>

          {/* Additional Charges */}
          {rule.charges.additionalCharges?.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-neutral-dark mb-3 flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Additional Charges
              </h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {rule.charges.additionalCharges.map((charge, idx) => (
                  <div
                    key={idx}
                    className="bg-gradient-to-br from-neutral-lightest to-white rounded-xl p-4 border border-neutral-light/50"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className="font-medium text-neutral-dark">
                        {charge.name}
                      </span>
                      <span className="text-lg font-bold text-primary">
                        {charge.type === "PERCENTAGE"
                          ? `${charge.value}%`
                          : `₹${charge.value}`}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="px-2 py-1 bg-white rounded text-neutral">
                        {charge.type}
                      </span>
                      <span className="px-2 py-1 bg-white rounded text-neutral">
                        Applies to: {charge.appliesTo}
                      </span>
                      {charge.isOptional && (
                        <span className="px-2 py-1 bg-warning/10 text-warning-dark rounded">
                          Optional
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>
      )}

      {/* GST Configuration */}
      {rule.gstConfig && (
        <Section title="GST Configuration" icon={Tag} color="success">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-neutral-lightest/50 rounded-xl p-4 text-center">
              <p className="text-xs text-neutral mb-2">Custom Rate</p>
              <p className="text-2xl font-bold text-success-dark">
                {rule.gstConfig.customRate || 3}%
              </p>
            </div>
            <div className="bg-neutral-lightest/50 rounded-xl p-4">
              <p className="text-xs text-neutral mb-2">Includes Making</p>
              <p
                className={`font-semibold ${
                  rule.gstConfig.includesMaking
                    ? "text-success-dark"
                    : "text-neutral"
                }`}
              >
                {rule.gstConfig.includesMaking ? "✓ Yes" : "✗ No"}
              </p>
            </div>
            <div className="bg-neutral-lightest/50 rounded-xl p-4">
              <p className="text-xs text-neutral mb-2">Includes Wastage</p>
              <p
                className={`font-semibold ${
                  rule.gstConfig.includesWastage
                    ? "text-success-dark"
                    : "text-neutral"
                }`}
              >
                {rule.gstConfig.includesWastage ? "✓ Yes" : "✗ No"}
              </p>
            </div>
            <div className="bg-neutral-lightest/50 rounded-xl p-4">
              <p className="text-xs text-neutral mb-2">Includes Stones</p>
              <p
                className={`font-semibold ${
                  rule.gstConfig.includesStones
                    ? "text-success-dark"
                    : "text-neutral"
                }`}
              >
                {rule.gstConfig.includesStones ? "✓ Yes" : "✗ No"}
              </p>
            </div>
          </div>
        </Section>
      )}

      {/* Metadata */}
      <Section title="Metadata" icon={FileText} color="neutral">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="flex items-center gap-3 p-3 bg-neutral-lightest/50 rounded-xl">
            <Clock className="w-5 h-5 text-neutral" />
            <div>
              <p className="text-xs text-neutral">Created At</p>
              <p className="text-sm font-medium text-neutral-dark">
                {new Date(rule.createdAt).toLocaleString("en-IN")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-neutral-lightest/50 rounded-xl">
            <Clock className="w-5 h-5 text-neutral" />
            <div>
              <p className="text-xs text-neutral">Last Updated</p>
              <p className="text-sm font-medium text-neutral-dark">
                {new Date(rule.updatedAt).toLocaleString("en-IN")}
              </p>
            </div>
          </div>
          {rule.createdBy && (
            <div className="flex items-center gap-3 p-3 bg-neutral-lightest/50 rounded-xl">
              <User className="w-5 h-5 text-neutral" />
              <div>
                <p className="text-xs text-neutral">Created By</p>
                <p className="text-sm font-medium text-neutral-dark">
                  {rule.createdBy.adminName || "N/A"}
                </p>
              </div>
            </div>
          )}
          {rule.lastModifiedBy && (
            <div className="flex items-center gap-3 p-3 bg-neutral-lightest/50 rounded-xl">
              <User className="w-5 h-5 text-neutral" />
              <div>
                <p className="text-xs text-neutral">Last Modified By</p>
                <p className="text-sm font-medium text-neutral-dark">
                  {rule.lastModifiedBy.adminName || "N/A"}
                  {rule.lastModifiedBy.modifiedAt && (
                    <span className="text-neutral text-xs ml-2">
                      (
                      {new Date(
                        rule.lastModifiedBy.modifiedAt
                      ).toLocaleDateString("en-IN")}
                      )
                    </span>
                  )}
                </p>
              </div>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
};

export default PricingRuleDetails;
